import React, { useState, memo } from 'react';
import { FadeIn } from './FadeIn';
import { GiftIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const bonuses = [{
  id: 1,
  badge: 'REGALO #1',
  title: 'Kit de Emergencia 3 AM',
  image: 'https://cdn.magicpatterns.com/uploads/cpHkiYZJNrzRtrwhfDS1gK/Bono_1.png',
  shortDescription: 'Una guía práctica y corta que te dice exactamente qué hacer en los primeros 90 segundos cuando tu gato te despierta de madrugada.',
  expandedContent: {
    why: 'El mayor enemigo del protocolo no es tu gato. Es tu reacción automática cuando estás agotado. A las 3 AM no pensás con lógica. Pensás con cansancio. Y cuando pensás con cansancio, casi siempre hacés algo que refuerza el problema: le hablás, lo mirás, te levantás, le das comida, abrís una puerta. Cada una de esas reacciones le enseña lo mismo a tu gato: "Si hago ruido, consigo una respuesta."',
    purpose: 'Este kit existe para cortarle el paso a eso. No para que lo leas a las 3 AM por primera vez. Para que lo leas hoy, lo entiendas en minutos y lo tengas listo cuando llegue el momento más difícil.',
    includes: ['El protocolo de 90 segundos — la secuencia exacta paso a paso para no reforzar el maullido cuando más vulnerable estás', 'Checklist "No hagas esto" — los errores más comunes que arruinan semanas de avance en una sola noche', 'Plan B para noches imposibles — qué hacer si el cansancio te gana y sentís que no vas a poder sostenerlo perfecto', 'Registro de 7 noches — una planilla simple para medir si realmente está mejorando, sin depender de tu memoria agotada', 'Tarjeta rápida imprimible — 7 puntos para dejar al lado de la cama y consultar en segundos'],
    value: 'Solo este kit puede ahorrarte semanas de prueba y error. Porque el problema no siempre es no saber el protocolo. El problema es no saber ejecutarlo cuando estás destruido a las 3 de la mañana.'
  }
}, {
  id: 2,
  badge: 'REGALO #2',
  title: 'Rutina Nocturna Express de 20 Minutos',
  image: 'https://cdn.magicpatterns.com/uploads/iPxM7BKGnAgvnQhcJK3CeK/Bono_2.png',
  shortDescription: 'La secuencia exacta que ejecutás antes de acostarte para bajar la energía nocturna de tu gato de forma estratégica, consistente y sin improvisar.',
  expandedContent: {
    why: 'Muchas personas sí intentan ayudar a su gato a dormir mejor. El problema no es la intención. Es que cada noche hacen algo distinto. Un día juegan cinco minutos. Otro día le dan comida primero. Otro día se olvidan. Otro día mueven el juguete sin intensidad real. Y así, con buena voluntad pero sin estructura, no crean ningún patrón.',
    purpose: 'Tu gato no necesita que seas perfecto. Necesita que seas consistente. Esta rutina te da exactamente eso: una secuencia repetible de 20 minutos que ordena el final del día y prepara a tu gato para dormir cuando vos dormís.',
    includes: ['La secuencia exacta de 20 minutos — activación, persecución, captura final, cena táctica y cierre del ambiente en orden estratégico', 'Checklist pre-noche — para preparar todo antes de empezar y no improvisar sobre la marcha', 'Versión rápida de 10 minutos — para días en los que llegás tarde o agotado y necesitás igual sostener el patrón', 'Los errores que más rompen la rutina — lo que parece correcto pero en realidad debilita el resultado', 'Tracker de 7 días — para saber si realmente estás ejecutando o solo creyendo que ejecutás'],
    value: 'La calma nocturna no empieza cuando apagás la luz. Empieza en los 20 minutos anteriores.'
  }
}];
export function BonusSection() {
  const [expandedBonus, setExpandedBonus] = useState<number | null>(null);
  return <section className="py-10 sm:py-14 relative" data-id="element-46">
      <div className="mx-auto max-w-7xl px-6 lg:px-8" data-id="element-47">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-48">
          {bonuses.map((bonus, index) => <FadeIn key={bonus.id} delay={index * 0.2} data-id="element-49">
              <div className="bg-navy-light border border-gold/20 rounded-2xl overflow-hidden h-full flex flex-col" data-id="element-50">
                {/* Image */}
                <div className="relative h-64 overflow-hidden" data-id="element-51">
                  <img src={bonus.image} alt={bonus.title} className="w-full h-full object-cover" data-id="element-52" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-gold/90 text-navy-dark px-4 py-2 rounded-full font-bold text-sm" data-id="element-53">
                    <GiftIcon className="h-4 w-4" data-id="element-54" />
                    {bonus.badge}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8 flex-grow flex flex-col" data-id="element-55">
                  <h3 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-56">
                    {bonus.title}
                  </h3>

                  <p className="text-slate-300 font-sans leading-relaxed mb-4" data-id="element-57">
                    {bonus.shortDescription}
                  </p>

                  {/* Expanded Content */}
                  <AnimatePresence data-id="element-58">
                    {expandedBonus === bonus.id && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-59">
                        <div className="space-y-4 text-sm text-slate-400 font-sans leading-relaxed border-t border-white/10 pt-4 mt-2" data-id="element-60">
                          <div data-id="element-61">
                            <h4 className="text-slate-100 font-semibold mb-2" data-id="element-62">
                              Por qué importa:
                            </h4>
                            <p data-id="element-63">{bonus.expandedContent.why}</p>
                          </div>

                          <p className="text-slate-300" data-id="element-64">
                            {bonus.expandedContent.purpose}
                          </p>

                          <div data-id="element-65">
                            <h4 className="text-slate-100 font-semibold mb-2" data-id="element-66">
                              Qué encontrás adentro:
                            </h4>
                            <ul className="space-y-2" data-id="element-67">
                              {bonus.expandedContent.includes.map((item, i) => <li key={i} className="flex gap-2" data-id="element-68">
                                  <span className="text-gold shrink-0" data-id="element-69">•</span>
                                  <span data-id="element-70">{item}</span>
                                </li>)}
                            </ul>
                          </div>

                          <div className="bg-gold/10 border border-gold/20 rounded-lg p-4 mt-4" data-id="element-71">
                            <p className="text-gold-light font-medium text-sm" data-id="element-72">
                              {bonus.expandedContent.value}
                            </p>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Toggle Button */}
                  <button onClick={() => setExpandedBonus(expandedBonus === bonus.id ? null : bonus.id)} className="mt-4 inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors font-medium text-sm" data-id="element-73">
                    {expandedBonus === bonus.id ? <>
                        Ver menos
                        <ChevronUpIcon className="h-4 w-4" data-id="element-74" />
                      </> : <>
                        Ver más
                        <ChevronDownIcon className="h-4 w-4" data-id="element-75" />
                      </>}
                  </button>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}